import React from "react";
import LocationTitle from "./location-title";
import { useHistory } from 'react-router-dom';

// redux
import { useSelector, useDispatch } from "react-redux";
import { selectSelectedTimeline } from "_redux/slices/timeline/timeline-areas";
import { setShowTimelineSidebar, setTimeLineDate, selectTimelineDate, selectExpandLocations, setExpandLocations, resetTimelineSlots } from "_redux/slices/timeline/timeline-control";
import { selectUsedTheme } from '_redux/slices/timeline/theme';
import { selectFirebaseInfo } from "_redux/slices/main-screen/user";
import { selectOpenBroadcastMessages, setOpenBroadcastMessages } from "_redux/slices/timeline/employees";

// tremor
import { Flex } from "@tremor/react";

// styling
import { Colors, Spacing } from "_styles";
import { IS_PHONE_TIMELINE } from "../timeline-constants";

// custom components
import { DateTime } from "_atoms";

// icons 
import { IoSettingsOutline } from "react-icons/io5";
import { CiFileOn } from "react-icons/ci";
import { IoChevronBackCircleSharp } from "react-icons/io5";
import { FcQuestions } from "react-icons/fc";
import { MdCameraAlt } from "react-icons/md";
import { BsLayoutSidebarReverse, BsLayoutSidebarInsetReverse } from "react-icons/bs";
import { IoChatbubble } from "react-icons/io5";

// bootstrap
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

// functions
import { getDate } from "_utils/functions";

// screenshot libraries
import html2canvas from "html2canvas";
import { jsPDF } from 'jspdf';

const TimelineTitles = (props) => {

  // nav
  const history = useHistory();

  //#region redux
  const dispatch = useDispatch();

  const selectedTimeline = useSelector(selectSelectedTimeline);
  const timelineDate = useSelector(selectTimelineDate);                    // select timeline Date
  const usedTheme = useSelector(selectUsedTheme);
  const dbUser = useSelector(selectFirebaseInfo);                                       // logged in user
  const expandLocations = useSelector(selectExpandLocations);
  const openBroadcastMessages = useSelector(selectOpenBroadcastMessages);
  //#endregion

  // Jersey-only: allow stepping the timeline to the next day for forward planning
  const canChangeDate = dbUser?.details?.authority === "JER";
  const viewingToday = getDate(timelineDate) === getDate(new Date());

  // toggle between today and tomorrow, resetting the visible window when returning to today
  const handleToggleDay = () => {
    if (viewingToday) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      dispatch(setTimeLineDate(tomorrow));
    } else {
      dispatch(setTimeLineDate(new Date()));
      dispatch(resetTimelineSlots({ authority: dbUser?.details?.authority }));
    }
  };

  const showEmployeeSettings = () => {
    history.push("/employee-settings");
  }

  // capture screenshot and export to PDF
  const handleCaptureScreenshot = async () => {
    try {
      const timelineRef = props?.timelineRef?.current;
      if (!timelineRef) {
        console.error("Timeline ref not available");
        return;
      }

      // store original scroll positions
      const originalScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const originalScrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

      // scroll to top to capture full content
      window.scrollTo(0, 0);
      timelineRef.scrollTop = 0;
      timelineRef.scrollLeft = 0;

      // wait a bit for scroll to complete
      await new Promise(resolve => setTimeout(resolve, 100));

      // capture screenshot
      const canvas = await html2canvas(timelineRef, {
        scale: 2, // higher quality
        useCORS: true,
        logging: false,
        backgroundColor: usedTheme.BACKGROUND_COLOR || "#ffffff",
        width: timelineRef.scrollWidth,
        height: timelineRef.scrollHeight,
        x: 0,
        y: 0,
        scrollX: 0,
        scrollY: 0,
      });

      // restore original scroll positions
      window.scrollTo(originalScrollLeft, originalScrollTop);

      const imgData = canvas.toDataURL("image/png");

      // calculate PDF dimensions (A4 landscape if very wide, otherwise fit to content)
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const pdfWidth = imgWidth * 0.264583; // convert pixels to mm (1px = 0.264583mm at 96dpi)
      const pdfHeight = imgHeight * 0.264583;

      // use A4 dimensions as max to avoid extremely large PDFs
      const maxWidth = 297; // A4 landscape width in mm
      const maxHeight = 210; // A4 landscape height in mm

      let finalWidth = pdfWidth;
      let finalHeight = pdfHeight;
      let scale = 1;

      // scale down if too large
      if (pdfWidth > maxWidth || pdfHeight > maxHeight) {
        const widthScale = maxWidth / pdfWidth;
        const heightScale = maxHeight / pdfHeight;
        scale = Math.min(widthScale, heightScale);
        finalWidth = pdfWidth * scale;
        finalHeight = pdfHeight * scale;
      }

      // create PDF 
      const pdf = new jsPDF({
        orientation: finalWidth > finalHeight ? "landscape" : "portrait",
        unit: "mm",
        format: [finalWidth, finalHeight],
      });

      // add image to PDF
      pdf.addImage(imgData, "PNG", 0, 0, finalWidth, finalHeight);

      // generate filename with date
      const date = getDate(timelineDate);
      const filename = `${dbUser.details.authority}-timeline-${date}-${new Date().getTime()}.pdf`;

      // save PDF
      pdf.save(filename);
    } catch (error) {
      console.error("Error capturing screenshot:", error);
      alert("Failed to export timeline to PDF. Please try again.");
    }
  };

  const isEmployee = props?.isEmployee

  return (
    <Row style={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: 0, flex: 1, margin: 0,
      backgroundColor: !isEmployee ? usedTheme.BACKGROUND_COLOR : 'transparent',
      borderBottomStyle: 'solid',
      borderBottomWidth: 2.5,
      borderBottomColor: isEmployee || props?.isReportPage ? Colors.PRM_LIGHT_BLUE : 'transparent'
    }}>

      {/* left side */}
      <Col>

        <Row>

          {/* go back to timeline icon*/}
          <Col xs={IS_PHONE_TIMELINE ? 12 : 10} style={{ display: "flex", alignItems: "center", paddingInlineStart: IS_PHONE_TIMELINE ? "6px" : "10px", gap: IS_PHONE_TIMELINE ? 2 : 0, minWidth: 0 }}>
            <IoChevronBackCircleSharp
              size={IS_PHONE_TIMELINE ? 30 : 35}
              onClick={() => {
                if (isEmployee || props?.isReportPage) {
                  history.push("/timeline-view-v2");
                } else {
                  history.push("/timeline-v2");
                }
              }}
              color={Colors.PRM_LIGHT_BLUE} cursor="pointer"
              style={{}}
              data-tippy-content="Go Back"
            />

            {/* settings / report buttons*/}
            {!isEmployee && !props?.isReportPage && (
              <>
                <IoSettingsOutline
                  size={IS_PHONE_TIMELINE ? 30 : 35}
                  // onClick={() => history.push("/timeline-control-panel")}
                  onClick={() => dispatch(setShowTimelineSidebar(true))}
                  color={Colors.PRM_LIGHT_BLUE} cursor="pointer"
                  style={{ borderRadius: "20px", padding: 5 }}
                  data-tippy-content="Settings"
                />

                {/* go to timeline analytics*/}
                <CiFileOn
                  size={IS_PHONE_TIMELINE ? 30 : 35}
                  onClick={() => history.push("/timeline-reports", { timeline: true })}
                  color={Colors.PRM_LIGHT_BLUE}
                  cursor="pointer"
                  style={{ borderRadius: "20px", padding: 5 }}
                  data-tippy-content="Reports"
                />

                <LocationTitle />
                <IoChatbubble
                  onClick={() => dispatch(setOpenBroadcastMessages(!openBroadcastMessages))}
                  style={{ cursor: "pointer", marginInlineStart: "10px" }}
                  color={Colors.PRM_LIGHT_BLUE}
                  size={20}
                  data-tippy-content="Broadcast Messages"
                />
              </>
            )}

          </Col>

        </Row>
      </Col>

      {/* right side */}
      {!isEmployee && !props?.isReportPage && (
        <Col xs="auto" style={{ display: 'flex', alignItems: 'center', paddingInlineEnd: IS_PHONE_TIMELINE ? '6px' : '10px', paddingInlineStart: IS_PHONE_TIMELINE ? 0 : undefined }}>
          {canChangeDate && (
            <button
              style={{ padding: IS_PHONE_TIMELINE ? '6px 10px' : '8px 15px', fontSize: IS_PHONE_TIMELINE ? 13 : undefined, backgroundColor: 'rgb(59 130 246)', border: 'transparent', borderRadius: '0.375rem', color: 'white', marginInlineEnd: '8px' }}
              onClick={handleToggleDay}
              data-tippy-content={viewingToday ? "View tomorrow's tasks, flights and bookings" : "Back to today"}
            >
              {viewingToday ? "Tomorrow" : "Back to Today"}
            </button>
          )}
          {!IS_PHONE_TIMELINE && <button
            style={{ padding: '8px 15px', backgroundColor: 'rgb(59 130 246)', border: 'transparent', borderRadius: '0.375rem' }}
            onClick={() => dispatch(setExpandLocations(!expandLocations))}
            data-tippy-content={expandLocations ? "Hide Locations" : "Show Locations"}
          >
            {expandLocations
              ? <BsLayoutSidebarReverse color="white" size={16} />
              : <BsLayoutSidebarInsetReverse color="white" size={16} />
            }
          </button>}
        </Col>
      )}

    </Row>
  );
};

export default TimelineTitles;